/**
 * RBACCoPilotGate — Role-Based Access Control wrapper for AI copilots.
 *
 * Kimani: free for all users
 * Obondo: paid tier only — free users see an upsell modal
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Bot, Lock, ArrowRight } from "lucide-react";
import { hasInstitutionalFeatures } from "@/lib/tier";

export function RBACCoPilotGate({ children, requiredTier = "paid" }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [dismissed, setDismissed] = useState(false);

    const isPaid = hasInstitutionalFeatures(user);

    if (requiredTier === "free" || isPaid || dismissed) {
        return children;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="rbac-upsell-modal">
            <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-md text-center space-y-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                    <Lock className="w-8 h-8 text-primary" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold mb-2">Obondo is for paid members</h2>
                    <p className="text-sm text-muted-foreground">
                        Obondo is our advanced AI co-pilot reserved for paid-tier traders.
                        Upgrade your plan to unlock institutional-grade insights, risk analysis,
                        and automated recommendations.
                    </p>
                </div>
                <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-500" />
                        <span><strong>Kimani</strong> — Free tier</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-primary" />
                        <span><strong>Obondo</strong> — Paid tier</span>
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <Button
                        className="w-full"
                        onClick={() => navigate("/app/subscription")}
                        data-testid="rbac-upgrade-btn"
                    >
                        Upgrade now <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button
                        variant="ghost"
                        className="w-full text-xs"
                        onClick={() => navigate("/app/copilot/kimani")}
                        data-testid="rbac-use-kimani-btn"
                    >
                        Use Kimani instead (free)
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default RBACCoPilotGate;
